/** @jsxImportSource @emotion/react */
import { useState, useCallback } from "react"
import { HexColorPicker, HexColorInput } from "react-colorful"
import { Dialog, DialogContent, DialogActions } from "@mui/material"
import { withMyTheme } from "../../../../theme/theme"
import { css } from "@mui/material"
import { mobileCss } from "../../../../util/isMobile"
import { MyButton } from "../../../../components/button/MyButton"
import { useTranslation } from "react-i18next"

const DialogContentStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    padding: 2rem 2rem 1rem;

    ${mobileCss(`
        padding: 1.5rem 1rem 0.5rem;
    `)}
`)

const PickerWrapperStyle = withMyTheme(() => css`
    .react-colorful {
        width: 260px;
        height: 260px;
        border-radius: 12px;
    }

    .react-colorful__saturation {
        border-radius: 12px 12px 0 0;
    }

    .react-colorful__last-control {
        border-radius: 0 0 12px 12px;
    }

    .react-colorful__pointer {
        width: 22px;
        height: 22px;
        border-width: 3px;
    }

    ${mobileCss(`
        .react-colorful {
            width: 220px;
            height: 220px;
        }
    `)}
`)

const HexInputWrapperStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.75rem;

    input {
        width: 110px;
        padding: 0.6rem 0.75rem;
        border: 2px solid ${theme.palette.primary.light};
        border-radius: 8px;
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1rem;
        text-align: center;
        text-transform: uppercase;
        outline: none;
        transition: border-color 0.2s ease;

        &:focus {
            border-color: ${theme.palette.primary.main};
        }
    }
`)

const SwatchStyle = (color: string) => css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: ${color};
    border: 2px solid rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    transition: background-color 0.15s ease;
`

const DialogActionsStyle = withMyTheme(() => css`
    padding: 1rem 2rem;
    justify-content: center;
    gap: 1rem;
`)

interface ColorPickerDialogProps {
    open: boolean
    color: string
    onConfirm: (color: string) => void
    onClose: () => void
}

export const ColorPickerDialog = ({ open, color, onConfirm, onClose }: ColorPickerDialogProps) => {
    const { t } = useTranslation()
    const [localColor, setLocalColor] = useState(color)

    const handleChange = useCallback((newColor: string) => {
        setLocalColor(newColor)
    }, [])

    const handleConfirm = () => {
        onConfirm(localColor)
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
            <DialogContent css={DialogContentStyle}>
                <div css={PickerWrapperStyle}>
                    <HexColorPicker color={localColor} onChange={handleChange} />
                </div>
                <div css={HexInputWrapperStyle}>
                    <div css={SwatchStyle(localColor)} />
                    <HexColorInput color={localColor} onChange={handleChange} prefixed />
                </div>
            </DialogContent>
            <DialogActions css={DialogActionsStyle}>
                <MyButton
                    variant="primary"
                    text={t('questionnaire.colors.cancel')}
                    onClick={onClose}
                />
                <MyButton
                    variant="primary"
                    text={t('questionnaire.colors.confirm')}
                    onClick={handleConfirm}
                />
            </DialogActions>
        </Dialog>
    )
}
